import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Plus, CreditCard as Edit2, Trash2, Save, X, Image as ImageIcon, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-toastify';
import { AdminMessage } from '../../models/types';

const AdminMessages = () => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [messages, setMessages] = useState<AdminMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    title_fr: '',
    title_en: '',
    message_fr: '',
    message_en: '',
    isActive: true,
    priority: 0
  });

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const messagesSnapshot = await getDocs(collection(db, 'adminMessages'));
      const messagesData = messagesSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title_fr: data.title_fr || '',
          title_en: data.title_en || '',
          message_fr: data.message_fr || '',
          message_en: data.message_en || '',
          imageUrl: data.imageUrl,
          isActive: data.isActive ?? true,
          priority: data.priority || 0,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          createdBy: data.createdBy || ''
        } as AdminMessage;
      });

      messagesData.sort((a, b) => (b.priority || 0) - (a.priority || 0));
      setMessages(messagesData);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('File must be an image');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    const timestamp = Date.now();
    const filename = `${timestamp}_${file.name}`;
    const storageRef = ref(storage, `admin-messages/${filename}`);

    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    return url;
  };

  const handleAdd = async () => {
    if (!formData.title_fr.trim() || !formData.title_en.trim() ||
        !formData.message_fr.trim() || !formData.message_en.trim()) {
      toast.error('Please fill in all required fields (titles and messages in both languages)');
      return;
    }

    if (!currentUser) {
      toast.error('You must be logged in');
      return;
    }

    setUploading(true);
    try {
      let imageUrl: string | undefined;

      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }

      await addDoc(collection(db, 'adminMessages'), {
        ...formData,
        imageUrl,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: currentUser.uid
      });

      toast.success('Message added successfully');
      setShowAddForm(false);
      setFormData({
        title_fr: '',
        title_en: '',
        message_fr: '',
        message_en: '',
        isActive: true,
        priority: 0
      });
      setImageFile(null);
      setImagePreview(null);
      fetchMessages();
    } catch (error) {
      console.error('Error adding message:', error);
      toast.error('Failed to add message');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async (id: string) => {
    const message = messages.find(m => m.id === id);
    if (!message) return;

    if (!message.title_fr.trim() || !message.title_en.trim() ||
        !message.message_fr.trim() || !message.message_en.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setUploading(true);
    try {
      let imageUrl = message.imageUrl;

      if (imageFile) {
        if (message.imageUrl) {
          try {
            const oldImageRef = ref(storage, message.imageUrl);
            await deleteObject(oldImageRef);
          } catch (error) {
            console.error('Error deleting old image:', error);
          }
        }
        imageUrl = await uploadImage(imageFile);
      }

      await updateDoc(doc(db, 'adminMessages', id), {
        title_fr: message.title_fr,
        title_en: message.title_en,
        message_fr: message.message_fr,
        message_en: message.message_en,
        isActive: message.isActive,
        priority: message.priority,
        imageUrl,
        updatedAt: Timestamp.now()
      });

      toast.success('Message updated successfully');
      setEditingId(null);
      setImageFile(null);
      setImagePreview(null);
      fetchMessages();
    } catch (error) {
      console.error('Error updating message:', error);
      toast.error('Failed to update message');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this message?')) return;

    try {
      const message = messages.find(m => m.id === id);
      if (message?.imageUrl) {
        try {
          const imageRef = ref(storage, message.imageUrl);
          await deleteObject(imageRef);
        } catch (error) {
          console.error('Error deleting image:', error);
        }
      }

      await deleteDoc(doc(db, 'adminMessages', id));
      toast.success('Message deleted successfully');
      fetchMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      await updateDoc(doc(db, 'adminMessages', id), {
        isActive,
        updatedAt: Timestamp.now()
      });
      toast.success(`Message ${isActive ? 'activated' : 'deactivated'}`);
      fetchMessages();
    } catch (error) {
      console.error('Error toggling message:', error);
      toast.error('Failed to update message status');
    }
  };

  const updateMessageField = (id: string, field: keyof AdminMessage, value: any) => {
    setMessages(messages.map(msg =>
      msg.id === id ? { ...msg, [field]: value } : msg
    ));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setImageFile(null);
    setImagePreview(null);
    fetchMessages();
  };

  const cancelAdd = () => {
    setShowAddForm(false);
    setFormData({
      title_fr: '',
      title_en: '',
      message_fr: '',
      message_en: '',
      isActive: true,
      priority: 0
    });
    setImageFile(null);
    setImagePreview(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">
          {t('language') === 'fr' ? 'Gestion des Messages' : 'Manage Messages'}
        </h1>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-700"
        >
          {showAddForm ? <X className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
          {showAddForm
            ? (t('language') === 'fr' ? 'Annuler' : 'Cancel')
            : (t('language') === 'fr' ? 'Nouveau Message' : 'New Message')
          }
        </button>
      </div>

      {showAddForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">
            {t('language') === 'fr' ? 'Ajouter un Message' : 'Add New Message'}
          </h2>
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('language') === 'fr' ? 'Titre (Français) *' : 'Title (French) *'}
                </label>
                <input
                  type="text"
                  value={formData.title_fr}
                  onChange={(e) => setFormData({ ...formData, title_fr: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Entrez le titre en français..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('language') === 'fr' ? 'Titre (Anglais) *' : 'Title (English) *'}
                </label>
                <input
                  type="text"
                  value={formData.title_en}
                  onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Enter the title in English..."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('language') === 'fr' ? 'Message (Français) *' : 'Message (French) *'}
              </label>
              <textarea
                value={formData.message_fr}
                onChange={(e) => setFormData({ ...formData, message_fr: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                rows={4}
                placeholder="Entrez le message en français..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('language') === 'fr' ? 'Message (Anglais) *' : 'Message (English) *'}
              </label>
              <textarea
                value={formData.message_en}
                onChange={(e) => setFormData({ ...formData, message_en: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                rows={4}
                placeholder="Enter the message in English..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('language') === 'fr' ? 'Image (Optionnelle)' : 'Image (Optional)'}
              </label>
              <div className="flex items-center gap-4">
                <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
                  <ImageIcon className="h-5 w-5" />
                  {t('language') === 'fr' ? 'Choisir une image' : 'Choose Image'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
                {imagePreview && (
                  <button
                    onClick={() => {
                      setImageFile(null);
                      setImagePreview(null);
                    }}
                    className="text-red-600 hover:text-red-800"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
              {imagePreview && (
                <div className="mt-3">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-h-48 rounded-lg border border-gray-300"
                  />
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {t('language') === 'fr'
                  ? 'Format accepté: JPG, PNG, GIF (max 5MB)'
                  : 'Accepted formats: JPG, PNG, GIF (max 5MB)'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('language') === 'fr' ? 'Priorité' : 'Priority'}
                </label>
                <input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('language') === 'fr'
                    ? 'Les messages avec une priorité plus élevée apparaissent en premier'
                    : 'Messages with higher priority appear first'}
                </p>
              </div>
              <div className="flex items-center pt-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="mr-2 h-4 w-4"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {t('language') === 'fr' ? 'Actif (visible sur la page d\'accueil)' : 'Active (visible on homepage)'}
                  </span>
                </label>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleAdd}
                disabled={uploading}
                className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {t('language') === 'fr' ? 'Ajout en cours...' : 'Adding...'}
                  </>
                ) : (
                  t('language') === 'fr' ? 'Ajouter' : 'Add'
                )}
              </button>
              <button
                onClick={cancelAdd}
                disabled={uploading}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 disabled:opacity-50"
              >
                {t('language') === 'fr' ? 'Annuler' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {messages.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
            {t('language') === 'fr'
              ? 'Aucun message. Cliquez sur "Nouveau Message" pour en ajouter un.'
              : 'No messages. Click "New Message" to add one.'}
          </div>
        ) : (
          messages.map((message) => (
            <div key={message.id} className="bg-white rounded-lg shadow-md p-6">
              {editingId === message.id ? (
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('language') === 'fr' ? 'Titre (Français) *' : 'Title (French) *'}
                      </label>
                      <input
                        type="text"
                        value={message.title_fr}
                        onChange={(e) => updateMessageField(message.id, 'title_fr', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('language') === 'fr' ? 'Titre (Anglais) *' : 'Title (English) *'}
                      </label>
                      <input
                        type="text"
                        value={message.title_en}
                        onChange={(e) => updateMessageField(message.id, 'title_en', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('language') === 'fr' ? 'Message (Français) *' : 'Message (French) *'}
                    </label>
                    <textarea
                      value={message.message_fr}
                      onChange={(e) => updateMessageField(message.id, 'message_fr', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      rows={4}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('language') === 'fr' ? 'Message (Anglais) *' : 'Message (English) *'}
                    </label>
                    <textarea
                      value={message.message_en}
                      onChange={(e) => updateMessageField(message.id, 'message_en', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      rows={4}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('language') === 'fr' ? 'Image' : 'Image'}
                    </label>
                    {(imagePreview || message.imageUrl) && (
                      <div className="mb-3">
                        <img
                          src={imagePreview || message.imageUrl}
                          alt="Current"
                          className="max-h-48 rounded-lg border border-gray-300"
                        />
                      </div>
                    )}
                    <div className="flex items-center gap-4">
                      <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
                        <ImageIcon className="h-5 w-5" />
                        {message.imageUrl
                          ? (t('language') === 'fr' ? 'Changer l\'image' : 'Change Image')
                          : (t('language') === 'fr' ? 'Ajouter une image' : 'Add Image')
                        }
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                        />
                      </label>
                      {(imagePreview || message.imageUrl) && (
                        <button
                          onClick={() => {
                            setImageFile(null);
                            setImagePreview(null);
                            if (editingId === message.id) {
                              updateMessageField(message.id, 'imageUrl', undefined);
                            }
                          }}
                          className="text-red-600 hover:text-red-800"
                        >
                          {t('language') === 'fr' ? 'Retirer l\'image' : 'Remove Image'}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('language') === 'fr' ? 'Priorité' : 'Priority'}
                      </label>
                      <input
                        type="number"
                        value={message.priority}
                        onChange={(e) => updateMessageField(message.id, 'priority', parseInt(e.target.value) || 0)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        min="0"
                      />
                    </div>
                    <div className="flex items-center pt-6">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={message.isActive}
                          onChange={(e) => updateMessageField(message.id, 'isActive', e.target.checked)}
                          className="mr-2 h-4 w-4"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          {t('language') === 'fr' ? 'Actif' : 'Active'}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleUpdate(message.id)}
                      disabled={uploading}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700 disabled:opacity-50"
                    >
                      {uploading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          {t('language') === 'fr' ? 'Enregistrement...' : 'Saving...'}
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          {t('language') === 'fr' ? 'Enregistrer' : 'Save'}
                        </>
                      )}
                    </button>
                    <button
                      onClick={cancelEdit}
                      disabled={uploading}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 disabled:opacity-50"
                    >
                      {t('language') === 'fr' ? 'Annuler' : 'Cancel'}
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      {message.imageUrl && (
                        <div className="mb-4">
                          <img
                            src={message.imageUrl}
                            alt={message.title_fr}
                            className="max-h-64 rounded-lg border border-gray-200"
                          />
                        </div>
                      )}

                      <div className="mb-3">
                        <span className="text-xs font-semibold text-gray-500">
                          {t('language') === 'fr' ? 'TITRE (FR):' : 'TITLE (FR):'}
                        </span>
                        <p className="text-lg font-bold text-gray-900 mt-1">{message.title_fr}</p>
                      </div>

                      <div className="mb-3">
                        <span className="text-xs font-semibold text-gray-500">
                          {t('language') === 'fr' ? 'TITRE (EN):' : 'TITLE (EN):'}
                        </span>
                        <p className="text-lg font-bold text-gray-900 mt-1">{message.title_en}</p>
                      </div>

                      <div className="mb-3">
                        <span className="text-xs font-semibold text-gray-500">
                          {t('language') === 'fr' ? 'MESSAGE (FR):' : 'MESSAGE (FR):'}
                        </span>
                        <p className="text-gray-800 mt-1">{message.message_fr}</p>
                      </div>

                      <div className="mb-3">
                        <span className="text-xs font-semibold text-gray-500">
                          {t('language') === 'fr' ? 'MESSAGE (EN):' : 'MESSAGE (EN):'}
                        </span>
                        <p className="text-gray-800 mt-1">{message.message_en}</p>
                      </div>

                      <div className="flex gap-4 text-sm text-gray-600">
                        <span>
                          {t('language') === 'fr' ? 'Priorité' : 'Priority'}: <strong>{message.priority}</strong>
                        </span>
                        <span className="flex items-center gap-1">
                          {message.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                          <span className={`font-semibold ${message.isActive ? 'text-green-600' : 'text-red-600'}`}>
                            {message.isActive
                              ? (t('language') === 'fr' ? 'Actif' : 'Active')
                              : (t('language') === 'fr' ? 'Inactif' : 'Inactive')
                            }
                          </span>
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleToggleActive(message.id, !message.isActive)}
                        className={`px-3 py-1 rounded text-sm font-medium ${
                          message.isActive
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {message.isActive
                          ? (t('language') === 'fr' ? 'Désactiver' : 'Deactivate')
                          : (t('language') === 'fr' ? 'Activer' : 'Activate')
                        }
                      </button>
                      <button
                        onClick={() => setEditingId(message.id)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Edit2 className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(message.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminMessages;
